'use client';

import type { ProductVariantInventory } from '@libs/algolia';

import { useProductDetails } from '@app/pdp/hooks';
import { usePageLayout } from '@libs/common-next';
import { useEffect, useMemo, useState } from 'react';
import { fetchInventories } from '@app/pdp/components/AddToCart/utils/fetchInventories';

type UseProductInventoryParams = {
    defaultValues: ProductVariantInventory;
};

type UseProductInventoryReturn = {
    data: ProductVariantInventory | undefined;
    isLoading: boolean;
};

export const useProductsInventories = ({
    defaultValues,
}: UseProductInventoryParams): UseProductInventoryReturn => {
    const { selectedStore } = usePageLayout();
    const { variants, sddStoreId } = useProductDetails();
    const [isLoading, setLoading] = useState(true);
    const [data, setData] = useState<ProductVariantInventory | undefined>(
        defaultValues
    );

    const productIds = useMemo(
        () => variants.map(variant => variant.objectID),
        [variants]
    );

    const params = useMemo(
        () => ({
            productIds,
            bopis: selectedStore.store?.StoreNumber?.toString() ?? '',
            sdd: sddStoreId?.toString() ?? '',
        }),
        [productIds, sddStoreId, selectedStore.store?.StoreNumber]
    );

    useEffect(() => {
        const fetchInventory = async () => {
            const inventories = await fetchInventories(params);
            setData(inventories);
            setLoading(false);
        };

        // We don't wait for selectedStore.isLoading because
        // the localStorage store is already a good enough bet
        fetchInventory();
    }, [params]);

    return { data, isLoading };
};
